import { useState, useEffect } from 'react';
import { Check, Flame, ThermometerSun, Timer, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { BunsenBurner, Crucible } from '@/components/lab';

// Educational info content for each step
const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Lighting the Bunsen Burner',
    content: 'A strong blue flame indicates complete combustion with good air flow. The hottest part of the flame is just above the inner blue cone - this is where we position the crucible for maximum heating efficiency.'
  },
  2: {
    title: 'Heating to Red Hot',
    content: 'Heating removes moisture and volatile contaminants from the crucible. When the crucible glows red (~700-900°C), all organic matter and water have been driven off. This typically takes 10-15 minutes with a strong flame.'
  },
  3: {
    title: 'Cooling in Desiccator',
    content: 'A desiccator contains silica gel that absorbs moisture from the air. Cooling here prevents the hot crucible from absorbing water vapor, which would increase its mass and cause inaccurate measurements.'
  },
  4: {
    title: 'Weighing the Crucible',
    content: 'An analytical balance measures to 0.001 g precision. Always let the crucible cool completely before weighing - hot objects create convection currents that cause unstable readings.'
  },
  5: {
    title: 'Achieving Constant Mass',
    content: 'Constant mass means two successive weighings differ by ≤0.01 g. This confirms all moisture is removed. If mass decreases between weighings, more heating is needed to drive off remaining volatiles.'
  }
};

export default function Part2InitialHeating() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [burnerLit, setBurnerLit] = useState(false);
  const [flameIntensity, setFlameIntensity] = useState<'off' | 'low' | 'high'>('off');
  const [heatingProgress, setHeatingProgress] = useState(0);
  const [crucibleTemp, setCrucibleTemp] = useState(25);
  const [coolingInProgress, setCoolingInProgress] = useState(false);
  const [cooled, setCooled] = useState(false);
  const [crucibleMass, setCrucibleMass] = useState<number | null>(null);
  const [massAttempts, setMassAttempts] = useState<number[]>([]);
  const [constantMassAchieved, setConstantMassAchieved] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Heating effect
  useEffect(() => {
    if (burnerLit && flameIntensity === 'high' && heatingProgress < 100) {
      const timer = setInterval(() => {
        setHeatingProgress(prev => {
          const newProgress = Math.min(prev + 2, 100);
          setCrucibleTemp(25 + (newProgress / 100) * 875); // Up to 900C
          return newProgress;
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [burnerLit, flameIntensity, heatingProgress]);

  // Cooling effect
  useEffect(() => {
    if (coolingInProgress && !cooled) {
      const timer = setTimeout(() => {
        setCooled(true);
        setCoolingInProgress(false);
        setCrucibleTemp(25);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [coolingInProgress, cooled]);

  const handleLightBurner = () => {
    if (currentStep === 1) {
      setBurnerLit(true);
      setFlameIntensity('high');
      setCurrentStep(2);
    }
  };

  const handleTransferToDesiccator = () => {
    if (heatingProgress >= 100 && currentStep === 2) {
      setCoolingInProgress(true);
      setBurnerLit(false);
      setFlameIntensity('off');
      setCurrentStep(3);
    }
  };

  const handleWeigh = () => {
    if (cooled && currentStep === 3) {
      // Simulate realistic mass with slight variation
      const baseMass = 25.3456;
      const variation = massAttempts.length === 0 ? 0.02 : (Math.random() * 0.005);
      const newMass = baseMass + variation;
      setCrucibleMass(parseFloat(newMass.toFixed(3)));
      setMassAttempts(prev => [...prev, newMass]);
      setCurrentStep(4);
    }
  };

  const handleCheckConstantMass = () => {
    if (massAttempts.length >= 1 && currentStep === 4) {
      if (massAttempts.length >= 2) {
        const diff = Math.abs(massAttempts[massAttempts.length - 1] - massAttempts[massAttempts.length - 2]);
        if (diff <= 0.01) {
          setConstantMassAchieved(true);
        } else {
          // Need to repeat
          setCooled(false);
          setHeatingProgress(0);
          setCurrentStep(2);
        }
      } else {
        // First attempt, need to repeat
        setCooled(false);
        setHeatingProgress(0);
        setCurrentStep(2);
      }
    }
  };

  const completedCount = [burnerLit, heatingProgress >= 100, cooled, crucibleMass, constantMassAchieved].filter(Boolean).length;

  // Get crucible color based on temperature
  const getCrucibleColor = () => {
    if (crucibleTemp < 200) return '#D3D3D3';
    if (crucibleTemp < 500) return '#C8B8A8';
    if (crucibleTemp < 700) return '#FFB366';
    if (crucibleTemp < 900) return '#FF6B35';
    return '#FF4444';
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Flame size={16} />
          <h3>Part II: Heat Crucible</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Light Bunsen Burner', hint: 'Strong blue flame', done: burnerLit },
            { id: 2, title: 'Heat Crucible', hint: 'Until glowing red (~10 min)', done: heatingProgress >= 100 },
            { id: 3, title: 'Cool in Desiccator', hint: 'Prevents moisture absorption', done: cooled },
            { id: 4, title: 'Weigh Crucible', hint: 'Record mass to 0.001 g', done: crucibleMass !== null },
            { id: 5, title: 'Check Constant Mass', hint: 'Within 0.01 g', done: constantMassAchieved }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {massAttempts.length > 0 && (
          <div className="mass-log">
            <h4>Mass Measurements</h4>
            {massAttempts.map((mass, i) => (
              <div key={i} className="mass-entry">
                <span>Attempt {i + 1}:</span>
                <span className="value">{mass.toFixed(3)} g</span>
              </div>
            ))}
            {massAttempts.length >= 2 && (
              <div className="mass-diff">
                Difference: {Math.abs(massAttempts[massAttempts.length - 1] - massAttempts[massAttempts.length - 2]).toFixed(3)} g
              </div>
            )}
          </div>
        )}
      </div>

      <LabBench
        experimentName="Heat Crucible to Constant Mass"
        completedCount={completedCount}
        totalSteps={5}
        equipment={['Bunsen burner', 'Crucible', 'Desiccator', 'Balance']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="heating-workspace">
          {/* Temperature Display */}
          {burnerLit && (
            <div className="temp-display">
              <ThermometerSun size={20} />
              <span>{Math.round(crucibleTemp)}°C</span>
            </div>
          )}

          {/* Heating Progress */}
          {burnerLit && heatingProgress < 100 && (
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${heatingProgress}%` }} />
              </div>
              <span>Heating: {heatingProgress}%</span>
            </div>
          )}

          <div className="equipment-row">
            {/* Retort Stand with Crucible */}
            <div className="ring-stand-setup">
              <svg viewBox="0 0 120 180" width={120} height={180}>
                {/* Stand base */}
                <rect x="20" y="170" width="80" height="8" fill="#444" rx="2" />
                {/* Vertical rod */}
                <rect x="56" y="20" width="8" height="150" fill="#666" />
                {/* Iron ring */}
                <ellipse cx="60" cy="70" rx="35" ry="8" fill="none" stroke="#888" strokeWidth="4" />
                {/* Clay triangle */}
                <polygon points="35,70 85,70 60,60" fill="#B8860B" stroke="#8B6914" strokeWidth="1" />
                {/* Crucible */}
                <path
                  d="M40 55 L40 45 Q40 35 60 35 Q80 35 80 45 L80 55 Q80 60 60 60 Q40 60 40 55"
                  fill={getCrucibleColor()}
                  stroke="#888"
                  strokeWidth="1"
                />
                {/* Lid */}
                <ellipse cx="60" cy="32" rx="18" ry="5" fill={getCrucibleColor()} stroke="#888" />
                {/* Glow effect when hot */}
                {crucibleTemp > 700 && (
                  <ellipse cx="60" cy="47" rx="22" ry="15" fill="rgba(255,100,50,0.3)" />
                )}
              </svg>
            </div>

            {/* Bunsen Burner */}
            <div
              className={`burner-container ${currentStep === 1 ? 'clickable' : ''}`}
              onClick={handleLightBurner}
            >
              <BunsenBurner
                isLit={burnerLit}
                flameIntensity={flameIntensity === 'high' ? 100 : 0}
              />
              <span className="equipment-label">Bunsen Burner</span>
            </div>

            {/* Desiccator */}
            <div
              className={`desiccator-container ${heatingProgress >= 100 && currentStep === 2 ? 'clickable' : ''}`}
              onClick={handleTransferToDesiccator}
            >
              <svg viewBox="0 0 100 100" width={80} height={80}>
                {/* Base */}
                <ellipse cx="50" cy="90" rx="40" ry="8" fill="#E8E8E8" />
                {/* Bottom section with silica gel */}
                <path d="M15 75 L15 90 L85 90 L85 75" fill="#F5F5F5" stroke="#CCC" />
                <rect x="20" y="78" width="60" height="8" fill="#4169E1" rx="2" />
                {/* Glass dome */}
                <path
                  d="M15 75 Q15 30 50 25 Q85 30 85 75"
                  fill="rgba(200,230,255,0.3)"
                  stroke="#7A9BB8"
                  strokeWidth="2"
                />
                {/* Knob */}
                <circle cx="50" cy="25" r="6" fill="#888" />
                {/* Crucible inside if cooling */}
                {coolingInProgress && (
                  <path
                    d="M40 60 L40 55 Q40 50 50 50 Q60 50 60 55 L60 60 Q60 62 50 62 Q40 62 40 60"
                    fill="#FFB366"
                    stroke="#888"
                  />
                )}
              </svg>
              <span className="equipment-label">Desiccator</span>
            </div>

            {/* Balance */}
            <div
              className={`balance-container ${cooled && currentStep === 3 ? 'clickable' : ''}`}
              onClick={handleWeigh}
            >
              <svg viewBox="0 0 100 80" width={100} height={80}>
                {/* Body */}
                <rect x="10" y="10" width="80" height="55" rx="4" fill="#E8E8E8" stroke="#CCC" />
                {/* Display */}
                <rect x="15" y="15" width="70" height="20" rx="2" fill="#001a00" />
                <text x="80" y="29" fontSize="12" fill="#00FF00" textAnchor="end" fontFamily="monospace">
                  {crucibleMass ? crucibleMass.toFixed(3) : '0.000'} g
                </text>
                {/* Pan chamber */}
                <rect x="20" y="40" width="60" height="20" rx="2" fill="#F8F8F8" stroke="#DDD" />
                {/* Pan */}
                <ellipse cx="50" cy="50" rx="25" ry="5" fill="#CCC" />
              </svg>
              <span className="equipment-label">Analytical Balance</span>
            </div>
          </div>

          {/* Action Buttons */}
          {crucibleMass !== null && currentStep === 4 && !constantMassAchieved && (
            <button className="action-btn" onClick={handleCheckConstantMass}>
              <Timer size={18} />
              {massAttempts.length < 2 ? 'Reheat & Reweigh' : 'Check Constant Mass'}
            </button>
          )}

          {constantMassAchieved && (
            <div className="status-message success">
              <Check size={20} />
              Constant mass achieved! Crucible mass: {crucibleMass?.toFixed(3)} g
            </div>
          )}

          <div className="info-box">
            <h4>Why Heat to Constant Mass?</h4>
            <p>
              Heating removes moisture and volatile contaminants. Constant mass means
              all moisture is removed and further heating won't change the mass.
            </p>
          </div>
        </div>

        <style>{`
          .heating-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .temp-display {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: rgba(255, 69, 0, 0.2);
            border-radius: 8px;
            color: #FF4500;
            font-weight: 600;
          }

          .progress-bar-container {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;
            max-width: 300px;
          }

          .progress-bar {
            flex: 1;
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #FF8C00, #FF4500);
            transition: width 0.1s;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
          }

          .ring-stand-setup {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .burner-container,
          .desiccator-container,
          .balance-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .clickable {
            cursor: pointer;
            animation: pulse 1.5s ease-in-out infinite;
          }

          .clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 90, 54, 0.4); }
            50% { box-shadow: 0 0 0 8px rgba(255, 90, 54, 0); }
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .mass-log {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .mass-log h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .mass-entry {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            padding: 4px 0;
          }

          .mass-entry .value {
            font-family: monospace;
            font-weight: 600;
          }

          .mass-diff {
            margin-top: 8px;
            padding: 8px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 4px;
            font-size: 11px;
            text-align: center;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .info-box {
            background: var(--bg-elevated);
            border-left: 3px solid var(--secondary);
            border-radius: 8px;
            padding: 16px;
            max-width: 400px;
          }

          .info-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }

          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {/* Info Panel */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
